import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ScrollText, RefreshCcw, ShieldAlert } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { SUPPORT_PHONE_DISPLAY } from "@/constants/support";

const PAGES = {
  terms: {
    icon: ScrollText,
    eyebrow: "The House Rules",
    title: "Terms of Service",
    intro:
      "These terms govern your membership of 50 CR CLUB. By purchasing access, you agree to everything below.",
    sections: [
      ["Membership", "Membership is a 30-day access pass to the private 50 CR GOLD GLOBAL trading community. Access starts the moment your Telegram group link is issued and ends 30 days after purchase unless renewed."],
      ["One Group Per Purchase", "Each purchase unlocks the group once. The Telegram account that claims it is the account that gets in."],
      ["Your Account Details", "We retain the payment email and country-coded phone number supplied at checkout. Phone is used to verify access with the Telegram bot; the payment email remains on the purchase record for support and administration."],
      ["No Re-Sharing", "Group links, VIP trades, analysis, live sessions and any club content are for paying members only. Sharing, forwarding, screenshotting for redistribution, or reselling access leads to immediate removal without refund."],
      ["No Guaranteed Returns", "We provide analysis, bias and education. We do not promise profits, and nothing shared inside the club is a guarantee of returns. Trading outcomes are yours alone."],
      ["Removal For Misconduct", "We reserve the right to remove any member who breaks these rules, spams, harasses members, or misuses club content — without refund."],
      ["Changes", "We may update these terms from time to time. The version live on this page at the time of your purchase applies to that purchase."],
      ["Contact", `Questions about these terms: call ${SUPPORT_PHONE_DISPLAY}.`],
    ],
  },
  refund: {
    icon: RefreshCcw,
    eyebrow: "Straight Answers",
    title: "Refund Policy",
    intro:
      "Payments are processed securely through the 50 CR Club checkout flow. This policy explains exactly where you stand.",
    sections: [
      ["Digital Access, Delivered Instantly", "50 CR CLUB is a digital membership. The moment your Telegram group link is issued, the product is considered delivered in full."],
      ["No Refunds After Access Is Claimed", "Once your group link has been issued or claimed, we do not offer refunds — the 30-day access is live and cannot be returned."],
      ["Before You Claim", `If you paid but have not claimed your Telegram access yet, call ${SUPPORT_PHONE_DISPLAY} within 48 hours of purchase with your purchase details and we will review your case.`],
      ["Failed Or Duplicate Payments", `If your payment was deducted but the purchase never registered, or you were charged twice, call ${SUPPORT_PHONE_DISPLAY} with your payment proof — verified cases are refunded or credited.`],
      ["Disputes", `Raise any billing dispute with us first by calling ${SUPPORT_PHONE_DISPLAY} before filing a chargeback. We resolve genuine cases fast.`],
    ],
  },
  disclaimer: {
    icon: ShieldAlert,
    eyebrow: "Read Before You Trade",
    title: "Risk Disclaimer",
    intro:
      "Trading can build wealth and can destroy capital. Read this fully before acting on anything shared inside 50 CR CLUB.",
    sections: [
      ["Substantial Risk Of Loss", "Trading in commodities, currencies and derivatives carries substantial risk of loss and is not suitable for every individual. You can lose part or all of your capital."],
      ["Education, Not Advice", "Everything shared in the club — market analysis, bias calls, VIP trades, live sessions, news breakdowns — is education and opinion, not investment advice, portfolio management, or a solicitation to buy or sell any security or asset."],
      ["Past Performance", "Past performance of any call, bias, setup or strategy does not guarantee future results. Screenshots and win streaks shown are historical, not promises."],
      ["Your Money, Your Responsibility", "You alone are responsible for your trades, position sizing and risk management. Never trade with money you cannot afford to lose. If in doubt, consult a SEBI-registered investment adviser."],
      ["No Liability", "50 CR CLUB, its team and partners accept no liability for any loss or damage arising from use of the club's content or community."],
    ],
  },
};

const Legal = ({ kind }) => {
  const page = PAGES[kind] || PAGES.terms;
  const Icon = page.icon;

  return (
    <main className="horizon-page relative min-h-screen overflow-x-hidden py-10" data-testid={`legal-${kind}-page`}>
      <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8">
        <div className="flex items-center justify-between py-4">
          <Link to="/" data-testid="legal-back-link" className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-[#0a0a0a]">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="flex items-center gap-2.5">
            <BrandLogo className="text-lg" />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-14"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-600">
            <Icon className="h-3.5 w-3.5" />
            {page.eyebrow}
          </span>
          <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-4xl lg:text-5xl" data-testid="legal-title">
            {page.title}
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">{page.intro}</p>
        </motion.div>

        <div className="mt-12 space-y-0 pb-16">
          {page.sections.map(([h, body], i) => (
            <motion.div
              key={h}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.03 * i }}
              data-testid={`legal-section-${i}`}
              className="group grid gap-3 border-l-2 border-t border-l-black/10 border-black/10 py-8 pl-6 transition-colors duration-500 last:border-b hover:border-l-[#0a0a0a] sm:grid-cols-[72px_1fr] sm:gap-6 sm:pl-8"
            >
              <span className="font-display text-3xl font-extrabold text-black/10 transition-colors duration-500 group-hover:text-black">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h2 className="font-sub text-lg font-semibold uppercase tracking-wide text-[#0a0a0a]">{h}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="pb-10 text-center font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
          © 2026 50 CR CLUB · {SUPPORT_PHONE_DISPLAY}
        </p>
      </div>
    </main>
  );
};

export default Legal;
